/** Canonical public site origin. Apex is the live domain; www and Render aliases redirect here. */
export const CANONICAL_HOST = "healthfq.com";
export const CANONICAL_ORIGIN = `https://${CANONICAL_HOST}`;
export const CANONICAL_OG_IMAGE_URL = `${CANONICAL_ORIGIN}/og-image.jpg`;

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "::1", "[::1]"]);
const ALIAS_HOSTS = new Set([`www.${CANONICAL_HOST}`]);

export function normalizeHostname(hostHeader: string | null | undefined): string {
  if (!hostHeader) return "";
  const host = hostHeader.split(",")[0].trim().toLowerCase();
  if (host.startsWith("[") && host.includes("]")) {
    return host.slice(0, host.indexOf("]") + 1);
  }
  return host.split(":")[0];
}

export function isLocalHostname(hostname: string): boolean {
  return LOCAL_HOSTS.has(hostname.toLowerCase());
}

export function isAliasHostname(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return ALIAS_HOSTS.has(host) || host === "onrender.com" || host.endsWith(".onrender.com");
}

export function shouldRedirectToCanonicalHost(hostname: string, protocol: string): boolean {
  if (!hostname || isLocalHostname(hostname)) return false;
  if (isAliasHostname(hostname)) return true;
  const proto = protocol.replace(":", "").toLowerCase();
  return hostname.toLowerCase() === CANONICAL_HOST && proto === "http";
}

export function canonicalLocationUrl(pathname: string, search = "", hash = ""): string {
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`;
  return `${CANONICAL_ORIGIN}${path}${search}${hash}`;
}

function currentHostname(): string {
  return typeof window !== "undefined" ? window.location.hostname : CANONICAL_HOST;
}

export function publicOrigin(hostname: string = currentHostname()): string {
  if (isLocalHostname(hostname) && typeof window !== "undefined") {
    return window.location.origin;
  }
  if (isLocalHostname(hostname)) {
    return `http://${hostname}`;
  }
  return CANONICAL_ORIGIN;
}

export function toPublicAbsoluteUrl(path: string, hostname: string = currentHostname()): string {
  try {
    return new URL(path, publicOrigin(hostname)).toString();
  } catch {
    return path;
  }
}

export function publicPageUrl(
  location: Pick<Location, "hostname" | "pathname" | "search" | "hash" | "href"> =
    typeof window !== "undefined" ? window.location : {
      hostname: CANONICAL_HOST,
      pathname: "/",
      search: "",
      hash: "",
      href: `${CANONICAL_ORIGIN}/`,
    },
): string {
  if (isLocalHostname(location.hostname)) return location.href;
  return canonicalLocationUrl(location.pathname, location.search, location.hash);
}

/** Returns true if navigation to the canonical host was started. */
export function redirectToCanonicalHost(): boolean {
  if (typeof window === "undefined") return false;
  const { hostname, protocol, pathname, search, hash } = window.location;
  if (!shouldRedirectToCanonicalHost(hostname, protocol)) return false;
  window.location.replace(canonicalLocationUrl(pathname, search, hash));
  return true;
}
